import { useEffect, useRef, useState } from 'react';
import type { BotTier, Item } from '@qoobd/rules';
import { audio } from '../audio/engine';
import { game, PLAYER, WATCH_NAMES } from '../game/controller';
import { challengeUrl, formatChallengeTime } from '../game/challenge';
import { copyText, drawCard, saveImage, shareCard, type CardData } from './share';
import { useGame } from '../game/store';
import { formatTime } from './Hud';
import { ItemIcon } from './icons';
import { AddNotes } from './Notes';
import { hostFriend, joinCodeFromLocation, joinFriend, quickMatch } from '../game/online';
import { OnlineResultActions } from './Online';
import { ArmorPicker } from './ArmorPicker';
import { AccountChip } from './Account';
import { AVATARS, PAINTS, SUITS } from '../game/armor';

const TIERS: { tier: BotTier; name: string; detail: string }[] = [
  { tier: 'novice', name: 'Novice', detail: 'Slow, clumsy, never swaps' },
  { tier: 'tactical', name: 'Tactical', detail: 'Reads its sensors, swaps when threatened' },
  { tier: 'relentless', name: 'Relentless', detail: 'Fast, and hunts you on sight' },
];

/** The cube mark from the logo: an isometric cube with an amber door and an ice door. */
function Mark({ size = 96 }: { size?: number }) {
  return (
    <svg className="mark" width={size} height={size} viewBox="-100 -110 200 220" aria-hidden="true">
      <g strokeLinejoin="round">
        <polygon points="0,-100 86.6,-50 0,0 -86.6,-50" fill="#3A3F47" stroke="#5A616B" strokeWidth="1.5" />
        <polygon points="-86.6,-50 0,0 0,100 -86.6,50" fill="#2A2E35" stroke="#5A616B" strokeWidth="1.5" />
        <polygon points="0,0 86.6,-50 86.6,50 0,100" fill="#1F2329" stroke="#5A616B" strokeWidth="1.5" />
        <g stroke="#5A616B" strokeWidth="1.5">
          <line x1="-57.7" y1="-66.7" x2="28.9" y2="-16.7" />
          <line x1="-28.9" y1="-83.3" x2="57.7" y2="-33.3" />
          <line x1="-57.7" y1="-33.3" x2="28.9" y2="-83.3" />
          <line x1="-28.9" y1="-16.7" x2="57.7" y2="-66.7" />
        </g>
        <polygon points="-58.9,66 -27.7,84 -27.7,24 -58.9,6" fill="#FFB000" />
        <polygon points="-53.7,69 -32.9,81 -32.9,29 -53.7,17" fill="#FFE2A3" />
        <polygon points="27.7,84 58.9,66 58.9,6 27.7,24" fill="#A9C4DB" />
        <polygon points="32.9,81 53.7,69 53.7,17 32.9,29" fill="#E6F0F8" />
      </g>
    </svg>
  );
}

/** Rules and controls, out of the way until someone asks. */
function HowToPlay({ onClose }: { onClose: () => void }) {
  const close = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    close.current?.focus();
    const esc = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', esc);
    return () => window.removeEventListener('keydown', esc);
  }, [onClose]);
  return (
    <div className="overlay notes-overlay" role="dialog" aria-modal="true" aria-labelledby="howto-title" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="card howto">
        <div className="eyebrow">How to play</div>
        <h2 id="howto-title">One cube. One rival.</h2>
        <section>
          <h3>Win</h3>
          <p>Reach the core room before your rival does, or catch them holding the wrong item.</p>
        </section>
        <section>
          <h3>Your item</h3>
          <p>
            You both carry a hidden rock, paper or scissors, and whoever's item wins takes any meeting. Swap at any time, but a swap
            locks you for a moment. Holding the same item cloaks you both: a silver shimmer, and you pass unseen.
          </p>
        </section>
        <section>
          <h3>Sensors</h3>
          <p>
            They tell you how close, never which way. <span className="t-red">Threat</span> lights up only when the rival's item beats
            yours. <span className="t-cyan">Core</span> warms as you near the goal.
          </p>
        </section>
        <section>
          <h3>Gear</h3>
          <p>
            An <b>EMP</b> fills nearby rival sensors with static for 5 seconds. A <b>seal</b>, used right after a step, locks the door
            behind you for 8 seconds. You start with one of each; lit crates hold more. Heat rings mark rooms your rival just left.
          </p>
        </section>
        <section>
          <h3>Moving</h3>
          <p>
            Every move plays out in full before the next one, and presses during a move are ignored. Turn, tilt and roll to face any
            door, even in the floor or ceiling, then step through.
          </p>
          <div className="keys">
            <kbd>A / D · ← / →</kbd>
            <span>Turn left / right</span>
            <kbd>W / S · ↑ / ↓</kbd>
            <span>Tilt up / down</span>
            <kbd>Q / E</kbd>
            <span>Roll left / right</span>
            <kbd>Space / Enter</kbd>
            <span>Step through the door you face</span>
            <kbd>1 / 2 / 3</kbd>
            <span>Swap to rock / paper / scissors</span>
            <kbd>R · F</kbd>
            <span>Fire EMP · Seal the door</span>
            <kbd>Touch</kbd>
            <span>Use the pad, or swipe to turn and tilt, twist two fingers to roll, tap to step</span>
          </div>
        </section>
        <p className="note">Headphones recommended. The cube makes sounds of its own, and none of them mean anything.</p>
        <div className="row">
          <button ref={close} className="cta" type="button" onClick={onClose}>
            Got it
          </button>
        </div>
      </div>
    </div>
  );
}

/** Drop the challenge (and its link) once someone chooses to play a fresh cube. */
function clearChallenge(): void {
  useGame.setState({ challenge: null });
  try {
    const u = new URL(location.href);
    u.searchParams.delete('c');
    u.searchParams.delete('watch');
    history.replaceState(null, '', u.pathname + u.search + u.hash);
  } catch {
    /* address bar stays as it was */
  }
}

/** A shared AI vs AI match: ?watch=<seed>. */
function watchSeedFromLocation(): number | null {
  try {
    const v = new URLSearchParams(location.search).get('watch');
    const n = v ? Number(v) : NaN;
    return Number.isSafeInteger(n) && n >= 0 && n <= 4_294_967_295 ? n : null;
  } catch {
    return null;
  }
}

export function Intro() {
  const tier = useGame((s) => s.tier);
  const challenge = useGame((s) => s.challenge);
  const watchTiers = useGame((s) => s.watch.tiers);
  const [howTo, setHowTo] = useState(false);
  const [watchSeed, setWatchSeed] = useState(watchSeedFromLocation);
  const [joinCode, setJoinCode] = useState(joinCodeFromLocation);
  const [onlinePick, setOnlinePick] = useState(false);
  const [armorOpen, setArmorOpen] = useState(false);
  const myArmor = useGame((s) => s.armor);
  const enter = () => {
    audio.unlock();
    if (challenge) game.start(challenge.tier, challenge.seed);
    else game.start(tier);
  };
  return (
    <div className="title-screen" role="dialog" aria-labelledby="intro-title">
      <div className="title-depth" aria-hidden="true">
        <i />
        <i />
        <i />
        <i />
      </div>
      <div className="title-account">
        <AccountChip />
      </div>
      <div className="title-main">
        <Mark />
        <h1 id="intro-title">
          Q<b>OO</b>BD
        </h1>
        <p className="hook">A thousand rooms. One rival. You can sense how close they are, never where.</p>

        {challenge && (
          <div className="challenge-banner" role="status">
            <div className="eyebrow">Challenge</div>
            <p>
              {challenge.ms !== null ? (
                <>
                  Beat <b>{formatChallengeTime(challenge.ms)}</b> on this exact cube against the <b>{TIER_NAME[challenge.tier]}</b> bot.
                </>
              ) : (
                <>
                  Survive this exact cube against the <b>{TIER_NAME[challenge.tier]}</b> bot.
                </>
              )}
            </p>
            <button type="button" className="linkish" onClick={clearChallenge}>
              Play a normal cube instead
            </button>
          </div>
        )}
        {joinCode && (
          <div className="challenge-banner" role="status">
            <div className="eyebrow">Online 1v1</div>
            <p>A friend invited you to hunt them in a fresh cube.</p>
            <div className="row" style={{ justifyContent: 'center' }}>
              <button
                type="button"
                className="cta"
                onClick={() => {
                  void joinFriend(joinCode);
                  setJoinCode(null);
                  clearChallenge();
                }}
              >
                Join the game
              </button>
              <button type="button" className="linkish" onClick={() => (setJoinCode(null), clearChallenge())}>
                No thanks
              </button>
            </div>
          </div>
        )}
        {watchSeed !== null && !challenge && (
          <div className="challenge-banner" role="status">
            <div className="eyebrow">Shared AI vs AI match</div>
            <p>Watch two bots hunt each other on this cube.</p>
            <div className="row" style={{ justifyContent: 'center' }}>
              <button
                type="button"
                className="cta"
                onClick={() => {
                  audio.unlock();
                  game.watch(watchTiers, watchSeed);
                  clearChallenge();
                }}
              >
                Watch it
              </button>
              <button type="button" className="linkish" onClick={() => (setWatchSeed(null), clearChallenge())}>
                No thanks
              </button>
            </div>
          </div>
        )}

        <div className="title-tiers" hidden={challenge !== null}>
          <div className="lbl">Rival difficulty</div>
          <div className="tier" role="group" aria-label="Rival difficulty">
            {TIERS.map((t) => (
              <button key={t.tier} type="button" aria-pressed={tier === t.tier} onClick={() => useGame.setState({ tier: t.tier })}>
                {t.name}
                <small>{t.detail}</small>
              </button>
            ))}
          </div>
        </div>

        <button className="cta title-cta" type="button" onClick={enter}>
          {challenge ? 'Accept the challenge' : 'Enter the cube'}
        </button>
        {onlinePick ? (
          <div className="online-pick" role="group" aria-label="Play online">
            <button className="cta" type="button" onClick={() => (setOnlinePick(false), void quickMatch())}>
              Find an opponent
            </button>
            <button className="cta" type="button" onClick={() => (setOnlinePick(false), void hostFriend())}>
              Play a friend
            </button>
            <button type="button" className="linkish" onClick={() => setOnlinePick(false)}>
              Back
            </button>
          </div>
        ) : (
          <button className="cta title-online" type="button" onClick={() => setOnlinePick(true)}>
            Play online
          </button>
        )}
        <div className="row title-secondary">
          <button className="cta alt" type="button" onClick={() => setArmorOpen(true)} aria-label={`Armor: ${AVATARS[myArmor.avatar].name}, ${SUITS[myArmor.suit].name}, ${PAINTS[myArmor.paint].name}`}>
            Armor
          </button>
          <button className="cta alt" type="button" onClick={() => setHowTo(true)}>
            How to play
          </button>
          <button
            className="cta alt"
            type="button"
            onClick={() => {
              audio.unlock();
              game.watch(watchTiers);
            }}
          >
            Watch AI vs AI
          </button>
          <AddNotes />
        </div>
        <p className="note title-foot">Play the bot, a friend or anyone online · headphones recommended</p>
      </div>
      {howTo && <HowToPlay onClose={() => setHowTo(false)} />}
      {armorOpen && <ArmorPicker onClose={() => setArmorOpen(false)} />}
    </div>
  );
}

const VERB: Record<Item, string> = { rock: 'crushes', paper: 'wraps', scissors: 'cut' };

const REACTIONS = [
  { id: 'fun', face: '😀', label: 'Fun' },
  { id: 'meh', face: '😐', label: 'Meh' },
  { id: 'too_hard', face: '😵', label: 'Too hard' },
  { id: 'too_easy', face: '😴', label: 'Too easy' },
  { id: 'confusing', face: '🤔', label: 'Confusing' },
] as const;

/** One tap, no typing: how the match felt, saved with its difficulty, result and cube. */
function Reactions({ r }: { r: NonNullable<ReturnType<typeof useGame.getState>['result']> }) {
  const [sent, setSent] = useState<string | null>(null);
  const send = (reaction: string) => {
    if (sent) return;
    setSent(reaction);
    void fetch('/api/reaction', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ reaction, tier: r.tier, outcome: r.winner === PLAYER ? 'won' : 'lost', reason: r.reason, seed: r.seed }),
      keepalive: true,
    }).catch(() => {});
  };
  return (
    <div className="reactions">
      <div className="lbl">{sent ? 'Thanks!' : 'How was that?'}</div>
      <div className="reaction-row" role="group" aria-label="How was that match?">
        {REACTIONS.map((x) => (
          <button
            key={x.id}
            type="button"
            aria-pressed={sent === x.id}
            disabled={sent !== null && sent !== x.id}
            onClick={() => send(x.id)}
          >
            <span className="face" aria-hidden="true">
              {x.face}
            </span>
            {x.label}
          </button>
        ))}
      </div>
    </div>
  );
}

type Result = NonNullable<ReturnType<typeof useGame.getState>['result']>;

const TIER_NAME: Record<string, string> = { novice: 'Novice', tactical: 'Tactical', relentless: 'Relentless' };

/** Share the result: a card image plus a link that drops a friend into the same cube. */
function ShareRow({ card, text, url }: { card: () => CardData; text: string; url: string }) {
  const [msg, setMsg] = useState('');
  const canShare = typeof navigator !== 'undefined' && 'share' in navigator;
  const flash = (t: string) => {
    setMsg(t);
    setTimeout(() => setMsg(''), 2500);
  };
  return (
    <div className="share">
      <div className="row">
        {canShare && (
          <button
            className="cta"
            type="button"
            onClick={async () => {
              const blob = await drawCard(card());
              const how = await shareCard(blob, text, url);
              if (how === 'unsupported') {
                saveImage(blob);
                flash((await copyText(url)) ? 'Image saved and link copied' : 'Image saved');
              }
            }}
          >
            Share
          </button>
        )}
        <button className="cta alt" type="button" onClick={async () => flash((await copyText(`${text} ${url}`)) ? 'Link copied' : url)}>
          Copy link
        </button>
        <button className="cta alt" type="button" onClick={async () => saveImage(await drawCard(card()))}>
          Save image
        </button>
      </div>
      {msg && (
        <p className="note" role="status">
          {msg}
        </p>
      )}
    </div>
  );
}

function coordsOf(rooms: readonly number[]): [number, number, number][] {
  const m = game.match;
  return m ? rooms.map((r) => m.maze.coords(r) as [number, number, number]) : [];
}

export function Results() {
  const mode = useGame((s) => s.mode);
  return mode === 'watch' ? <WatchResults /> : <PlayResults />;
}

function PlayResults() {
  const r = useGame((s) => s.result)!;
  const online = useGame((s) => s.mode === 'online');
  const challenge = useGame((s) => s.challenge);
  const again = useRef<HTMLButtonElement>(null);
  useEffect(() => again.current?.focus(), []);
  const won = r.winner === PLAYER;
  const title = r.forfeit
    ? won
      ? 'They bailed'
      : 'You left'
    : r.reason === 'core'
      ? won
        ? 'Signal locked'
        : 'Rival got there first'
      : won
        ? 'Caught them'
        : 'Caught out';
  const eyebrow = r.forfeit ? (won ? 'Opponent left · you win' : 'Forfeit') : r.reason === 'core' ? (won ? 'Core claimed' : 'Core lost') : won ? 'Encounter won' : 'Encounter lost';
  const how = r.forfeit
    ? won
      ? 'Your opponent left the match.'
      : 'You left the match.'
    : r.reason === 'core'
      ? won
        ? 'You reached the core first.'
        : 'The rival claimed the core.'
      : won
        ? `Your ${r.playerItem} ${VERB[r.playerItem]} their ${r.rivalItem}.`
        : `Their ${r.rivalItem} ${VERB[r.rivalItem]} your ${r.playerItem}.`;

  // A challenge counts only on its own cube and difficulty.
  const ch = challenge && challenge.seed === r.seed && challenge.tier === r.tier ? challenge : null;
  let verdict: string | null = null;
  if (ch && ch.ms !== null) {
    const diff = Math.abs(ch.ms - r.elapsedMs);
    verdict = !won
      ? `Challenge stands: ${formatChallengeTime(ch.ms)} to beat.`
      : r.elapsedMs < ch.ms
        ? `You beat the challenge by ${formatChallengeTime(diff)}!`
        : `So close: ${formatChallengeTime(diff)} slower than the challenge.`;
  } else if (ch) verdict = won ? 'You survived the challenge cube.' : 'The challenge cube got you too.';

  const mine = { seed: r.seed, tier: r.tier, ms: won ? r.elapsedMs : null, steps: won ? r.steps : null };
  const url = challengeUrl(mine);
  const text = won ? `I beat this QOOBD cube in ${formatChallengeTime(r.elapsedMs)}. Can you beat my time?` : 'This QOOBD cube beat me. Can you survive it?';
  const card = (): CardData => ({
    eyebrow,
    title,
    lose: !won,
    stats: `${won ? formatChallengeTime(r.elapsedMs) : formatTime(r.elapsedMs)} · ${r.steps} steps · vs ${TIER_NAME[r.tier]}`,
    hook: won ? 'Can you beat my time on this exact cube?' : 'This cube beat me. Can you survive it?',
    url,
    pathA: coordsOf(game.path),
    pathB: coordsOf(game.rivalPath),
    core: game.match ? (game.match.maze.coords(game.match.maze.core) as [number, number, number]) : null,
    labelA: 'YOU',
    labelB: TIER_NAME[r.tier]!.toUpperCase() + ' BOT',
  });

  const fresh = () => {
    clearChallenge();
    game.start(r.tier);
  };
  return (
    <div className="overlay" role="dialog" aria-labelledby="result-title">
      <div className="card">
        <div className="eyebrow">{eyebrow}</div>
        <h2 id="result-title" className={won ? '' : 'lose'}>
          {title}
        </h2>
        {verdict && !online && <p className="verdict">{verdict}</p>}
        <div className="vs">
          <div className="side you">
            <ItemIcon item={r.playerItem} size={38} />
            <div>
              <span className="lbl">You</span>
              <strong>{r.playerItem.toUpperCase()}</strong>
            </div>
          </div>
          <div className="mid">VS</div>
          <div className="side them">
            <div>
              <span className="lbl">{online ? 'Opponent' : 'Rival'}</span>
              <strong>{r.rivalItem.toUpperCase()}</strong>
            </div>
            <ItemIcon item={r.rivalItem} size={38} />
          </div>
        </div>
        <div className="result">
          <div>
            <span className="lbl">Time</span>
            <span>{formatTime(r.elapsedMs)}</span>
          </div>
          <div>
            <span className="lbl">Steps</span>
            <span>{r.steps}</span>
          </div>
          <div>
            <span className="lbl">Actions</span>
            <span>{r.actions}</span>
          </div>
          <div>
            <span className="lbl">Bonks</span>
            <span>{r.bonks}</span>
          </div>
        </div>
        <Reactions r={online ? { ...r, tier: 'online' as never } : r} />
        <p className="note">
          {online
            ? `${how.replace('the rival', 'your opponent').replace('The rival', 'Your opponent')} You explored ${r.explored} of 1,000 rooms.`
            : `${how} Rival: ${r.tier}. Cube seed ${r.seed} · you explored ${r.explored} of 1,000 rooms.`}
        </p>
        {online ? (
          <OnlineResultActions />
        ) : (
        <>
        <div className="row">
          <button ref={again} className="cta" type="button" onClick={fresh}>
            New cube
          </button>
          <button className="cta alt" type="button" onClick={() => game.start(r.tier, r.seed)}>
            Replay this cube
          </button>
          <AddNotes />
        </div>
        <div className="lbl">{won ? 'Challenge a friend to beat your time' : 'Dare a friend to try this cube'}</div>
        <ShareRow card={card} text={text} url={url} />
        </>
        )}
      </div>
    </div>
  );
}

function WatchResults() {
  const r = useGame((s) => s.result)!;
  const tiers = useGame((s) => s.watch.tiers);
  const again = useRef<HTMLButtonElement>(null);
  useEffect(() => again.current?.focus(), []);
  const winner = WATCH_NAMES[r.winner]!;
  const loser = WATCH_NAMES[r.loser]!;
  const amberWon = r.winner === PLAYER;
  const how =
    r.reason === 'core'
      ? `${winner} reached the core first.`
      : `${winner}'s ${(amberWon ? r.playerItem : r.rivalItem)} ${VERB[amberWon ? r.playerItem : r.rivalItem]} ${loser}'s ${amberWon ? r.rivalItem : r.playerItem}.`;
  const url = `${location.origin}/?watch=${r.seed}`;
  const card = (): CardData => ({
    eyebrow: r.reason === 'core' ? 'AI vs AI · core claimed' : 'AI vs AI · encounter',
    title: `${winner} wins`,
    lose: false,
    stats: `${formatTime(r.elapsedMs)} · ${TIER_NAME[tiers[0]]} vs ${TIER_NAME[tiers[1]]}`,
    hook: 'Two bots, one cube, no map. Watch them hunt.',
    url,
    pathA: coordsOf(game.path),
    pathB: coordsOf(game.rivalPath),
    core: game.match ? (game.match.maze.coords(game.match.maze.core) as [number, number, number]) : null,
    labelA: 'AMBER',
    labelB: 'ICE',
  });
  return (
    <div className="overlay" role="dialog" aria-labelledby="result-title">
      <div className="card">
        <div className="eyebrow">AI vs AI · {r.reason === 'core' ? 'Core claimed' : 'Encounter'}</div>
        <h2 id="result-title" className={amberWon ? '' : 'ice'}>
          {winner} wins
        </h2>
        <div className="vs">
          <div className="side you">
            <ItemIcon item={r.playerItem} size={38} />
            <div>
              <span className="lbl">AMBER · {TIER_NAME[tiers[0]]}</span>
              <strong>{r.playerItem.toUpperCase()}</strong>
            </div>
          </div>
          <div className="mid">VS</div>
          <div className="side them">
            <div>
              <span className="lbl">ICE · {TIER_NAME[tiers[1]]}</span>
              <strong>{r.rivalItem.toUpperCase()}</strong>
            </div>
            <ItemIcon item={r.rivalItem} size={38} />
          </div>
        </div>
        <p className="note">
          {how} Time {formatTime(r.elapsedMs)} · cube seed {r.seed}.
        </p>
        <div className="row">
          <button ref={again} className="cta" type="button" onClick={() => game.watch(tiers)}>
            Watch another
          </button>
          <button className="cta alt" type="button" onClick={() => game.watch(tiers, r.seed)}>
            Rematch on this cube
          </button>
          <button className="cta alt" type="button" onClick={() => useGame.setState({ phase: 'intro', mode: 'play' })}>
            Back to title
          </button>
        </div>
        <ShareRow card={card} text={`${winner} beat ${loser} in a QOOBD AI vs AI match.`} url={url} />
      </div>
    </div>
  );
}
